import React, {useMemo} from 'react'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {ShopingBag} from '../../components/icons'
import {formattedDate} from '../../utils/custom-utils'

const LastPurchased = ({product, customer}) => {
    const variantProductIds = useMemo(
        () =>
            new Set(product?.variants?.map((variant) => variant?.productId).filter(Boolean) || []),
        [product]
    )

    const latestPurchasedOrder = useMemo(() => {
        const customerOrders = customer?.orders?.data || []
        if ((!variantProductIds.size && !product?.id) || !customerOrders.length) {
            return null
        }

        const filteredMatchingOrders = customerOrders
            .filter((order) =>
                order?.productItems?.some(
                    (item) =>
                        item?.productId &&
                        (variantProductIds.has(item.productId) || product?.id === item.productId)
                )
            )
            .map((order) => ({
                ...order,
                purchaseDate: new Date(order.creationDate)
            }))

        if (!filteredMatchingOrders.length) return null

        const latestOrder = filteredMatchingOrders.reduce((latest, order) =>
            order.purchaseDate > latest.purchaseDate ? order : latest
        )

        // find the specific productId that matched inside this latest order
        const matchedItem = latestOrder.productItems.find(
            (item) =>
                item?.productId &&
                (variantProductIds.has(item.productId) || product?.id === item.productId)
        )

        return {
            alreadyPurchased: true,
            orderNo: latestOrder.orderNo,
            productId: matchedItem?.productId,
            purchaseDate: latestOrder.purchaseDate
        }
    }, [customer, variantProductIds, product])

    if (!customer?.isRegistered || !latestPurchasedOrder) return null

    return (
        <Box
            padding={4}
            border="2px solid #BDB49B"
            marginBottom={{base: '3', md: '4'}}
            borderRadius="lg"
            display="flex"
            alignItems="center"
            className="lastPurchased"
        >
            <ShopingBag marginRight={4} />
            <Box>
                <Text
                    fontSize={{base: 'lg', md: 'xl'}}
                    fontWeight={{base: 'bold', md: 'extrabold'}}
                    lineHeight={7}
                    marginBottom={0}
                >
                    <FormattedMessage id="last_purchased_text" defaultMessage="Last Purchased" />
                    <Text as="span" ml={1}>
                        {formattedDate(latestPurchasedOrder.purchaseDate)}
                    </Text>
                </Text>
                <Link
                    to={`/orderdetails?orderID=${latestPurchasedOrder.orderNo}&fromPDP=true#open-orders`}
                    textDecoration="underline"
                    textUnderlineOffset="2px"
                    lineHeight={6}
                    fontWeight="medium"
                >
                    <FormattedMessage id="view_order_text" defaultMessage="View Order" />
                </Link>
            </Box>
        </Box>
    )
}

LastPurchased.propTypes = {
    product: PropTypes.object,
    customer: PropTypes.object
}

export default LastPurchased
